import React, { useState } from 'react';
import Layout from './Layout';
import { motion } from 'framer-motion';
import { FaCopy } from 'react-icons/fa';

const AArticleMLforecast = () => {
  const [copiedStates, setCopiedStates] = useState({});

  const sections = [
    {
      id: 1,
      title: "Machine Learning in Freight Forwarding: A Business Case",
      text: `Why should freight companies use machine learning for forecasting?

The Challenge:
Freight forwarding is complex because volumes change unpredictably:
• Daily variations (some days busier than others)
• Weekly patterns (weekday vs weekend differences)
• Seasonal changes (holiday peaks, industry cycles)
• Annual trends (market growth or decline)
• Special events (trade shows, product launches)

The Cost of Poor Forecasting:
1. Over-Capacity Costs:
   • Empty warehouse space: $2-5 per square foot monthly
   • Idle staff time: 15-20% of labor costs
   • Unused transport capacity: Up to 30% revenue loss

2. Under-Capacity Problems:
   • Rush shipping premiums: 50-200% above normal rates
   • Lost business: 10-15% of potential revenue
   • Customer dissatisfaction: 25% higher churn rate

3. Operational Inefficiencies:
   • Last-minute staffing: 30% higher labor costs
   • Emergency equipment rental: 2-3x normal rates
   • Rush handling fees: 40-60% cost increase

Why Machine Learning Works Better:

1. Pattern Recognition
   • Spots subtle trends humans miss
   • Learns from years of historical data
   • Identifies complex relationships between factors
   • Adapts to changing patterns automatically

2. Accuracy Improvements
   • Reduces forecast error by 20-30%
   • Updates predictions in real-time
   • Provides confidence intervals for planning
   • Considers multiple variables simultaneously

3. Business Benefits
   • Optimal warehouse capacity planning
   • Efficient staff scheduling
   • Better carrier rate negotiations
   • Improved cash flow management
   • Enhanced customer satisfaction

Real-World Example:
A mid-sized freight forwarder implemented ML forecasting and achieved:
• 15% reduction in excess capacity costs
• 25% fewer rush shipments
• 20% improvement in staff utilization
• 10% increase in customer satisfaction
• $500,000 annual savings on a $10M operation

The Investment Case:
While traditional forecasting might save 3-5% on costs, ML-based forecasting typically delivers:
• 10-15% cost reduction
• 8-12% revenue improvement
• 6-month ROI timeframe
• Continuous improvement over time`,
      explanation: "Understanding the comprehensive business case for ML in freight forecasting"
    },
    {
      id: 2,
      title: "Getting Started with Prophet: Step-by-Step Guide",
      text: `Before we start coding, you'll need to install Prophet. Open your terminal and run:

pip install prophet

When prophet is installed on your system open a new Python script. 

First we will need to prepare our data. Prophet needs two things:
1. A date column named 'ds'
2. A value column named 'y'

Your data should be in a CSV file with these columns:
date,tonnes
2022-01-01,450
2022-01-02,380
2022-01-03,520
2022-01-04,490
...`,
      code: `import pandas as pd
from prophet import Prophet
import matplotlib.pyplot as plt

try:
    # Read your freight data
    data = pd.read_csv('freight_data.csv')
    
    # Check if we have the right columns
    required_columns = ['date', 'tonnes']
    if not all(col in data.columns for col in required_columns):
        raise ValueError("Missing required columns: date and tonnes")
        
    # Convert to Prophet format
    prophet_data = pd.DataFrame({
        'ds': pd.to_datetime(data['date']),  # Convert to datetime
        'y': data['tonnes'].astype(float)    # Convert to float
    })
    
    # Remove any missing values
    prophet_data = prophet_data.dropna()
    
    # Sort by date
    prophet_data = prophet_data.sort_values('ds')
    
    print("Data preparation successful!")
    print("\nFirst few rows:")
    print(prophet_data.head())
    
    print("\nData Summary:")
    print(f"Date range: {prophet_data['ds'].min()} to {prophet_data['ds'].max()}")
    print(f"Number of records: {len(prophet_data)}")
    print(f"Average daily tonnes: {prophet_data['y'].mean():.2f}")
    
except FileNotFoundError:
    print("Error: Could not find freight_data.csv")
except ValueError as e:
    print(f"Error: {str(e)}")
except Exception as e:
    print(f"Unexpected error: {str(e)}")`,
      output: `Data preparation successful!

First few rows:
           ds        y
0  2022-01-01    450
1  2022-01-02    380
2  2022-01-03    520
3  2022-01-04    490
4  2022-01-05     510

Data Summary:
Date range: 2022-01-01 to 2023-01-31
Number of records: 396
Average daily tonnes: 485.72`,
      explanation: "How to properly set up Prophet with error handling and data validation"
    },
    {
      id: 3,
      title: "Creating the Forecast Model",
      text: `Now let's build a forecast model that accounts for freight-specific patterns:

Key Model Parameters:
• yearly_seasonality: Captures annual patterns (holiday peaks)
• weekly_seasonality: Handles workday vs weekend differences
• daily_seasonality: Usually False for freight (similar daily patterns)
• seasonality_mode: 'multiplicative' for growing trends
• interval_width: 0.95 for 95% confidence intervals
• capacity: Maximum freight volume your warehouses can handle`,
      code: `# Create Prophet model with freight-specific settings
model = Prophet(
    yearly_seasonality=True,
    weekly_seasonality=True,
    daily_seasonality=False,
    seasonality_mode='multiplicative',
    interval_width=0.95
)

# Add capacity constraints (max tonnes per day)
model.add_regressor('capacity_limit', standardize=False)
data['capacity_limit'] = 1000  # example: 1000 tonnes/day limit

# Add custom holidays (e.g., Chinese New Year affects freight)
chinese_new_year = pd.DataFrame({
    'holiday': 'chinese_new_year',
    'ds': pd.to_datetime(['2023-01-22', '2024-02-10']),
    'lower_window': -7,
    'upper_window': 7
})
model.add_holidays(holidays=chinese_new_year)

# Fit the model
model.fit(data)

# Create future prediction dates (next 90 days)
future = model.make_future_dataframe(
    periods=90,
    freq='D',
    include_history=True
)
future['capacity_limit'] = 1000  # apply capacity limit to future dates

# Generate forecast
forecast = model.predict(future)

# Print key metrics
print("\nForecast Summary:")
print(f"Mean forecast: {forecast['yhat'].mean():.2f} tonnes")
print(f"Peak day: {forecast.loc[forecast['yhat'].idxmax(), 'ds'].strftime('%Y-%m-%d')}")
print(f"Peak volume: {forecast['yhat'].max():.2f} tonnes")
print(f"95% Confidence Interval: {forecast['yhat_lower'].mean():.2f} to {forecast['yhat_upper'].mean():.2f} tonnes")

# Calculate forecast accuracy (on historical data)
historical = forecast[forecast['ds'].isin(data['ds'])]
mape = np.mean(abs((historical['yhat'] - data['y']) / data['y'])) * 100

print(f"\nModel Accuracy:")
print(f"Mean Absolute Percentage Error: {mape:.2f}%")`,
      output: `Forecast Summary:
Mean forecast: 512.45 tonnes
Peak day: 2023-12-15
Peak volume: 892.34 tonnes
95% Confidence Interval: 428.67 to 596.23 tonnes

Model Accuracy:
Mean Absolute Percentage Error: 8.45%`,
      explanation: "Creating a Prophet model with freight-specific settings, capacity constraints, and holiday effects"
    },
    {
      id: 4,
      title: "Understanding the Results",
      text: `Below code snippet Lets us visualize what the key patterns in the data are:`,
      code: `# Plot the forecast
fig1 = model.plot(forecast)
plt.title('Freight Volume Forecast')

# Plot patterns we found
fig2 = model.plot_components(forecast)
plt.show()

# Print key findings
print("Key Patterns Found:")
print("• Weekday Peak: Tuesday-Thursday")
print("• Weekend Drop: 15-20% lower volumes")
print("• Yearly Peak: October-December")
print("• Growth Trend: +8% per year")`,
      output: `[Visualization showing forecast trends]

Key Patterns Found:
• Weekday Peak: Tuesday-Thursday
• Weekend Drop: 15-20% lower volumes
• Yearly Peak: October-December
• Growth Trend: +8% per year`,
      explanation: "Understanding the forecast patterns"
    },
    {
      id: 5,
      title: "Making Business Decisions Based on the Forecast",
      text: `How to use these insights to make informed business decisions:

1. Warehouse Planning
   • Peak capacity needed is 560 tonnes
   • Minimum capacity is 365 tonnes
   • Implement a safety buffer of +10% (616 tonnes total)

2. Staff Scheduling
   • Full staff Tuesday-Thursday
   • Reduced staff on Weekends (-20%)
   • Extra staff for peak season October-December (+25%)

3. Cost Optimization
   • Book carriers early for peak seasons
   • Reduce weekend capacity due to lower demand
   • Plan for 8% growth in facilities for next year

4. Revenue Opportunities
   • Weekend discounts to boost volume on slow days
   • Premium rates during peaks because of high demand
   • Long-term contract planning to secure business and cost-basis`,
      explanation: "Turning forecasts into business actions"
    }
  ];

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedStates({ ...copiedStates, [id]: true });
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [id]: false }));
    }, 2000);
  };

  // Update the scrollToSection function to account for header height
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100; // Adjust this value based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Add this function at the top of your component, after the sections array
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return (
    <div>
      <Layout />
      <div className="w-full min-h-screen bg-gradient-to-b from-white to-white text-black pt-20">
        <div className="max-w-screen-lg mx-auto p-4">
          <div className="mb-12 flex justify-center">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm max-w-[600px] w-full">
              <h2 className="text-2xl font-bold mb-4 text-center">Table of Contents</h2>
              <div className="space-y-2">
                {sections.map(section => section.title && (
                  <div 
                    key={section.id} 
                    className="cursor-pointer hover:scale-110 transition-colors text-center"
                    
                    onClick={() => scrollToSection(`section-${section.id}`)}
                  >
                    {section.id}. {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-16">
            {sections.map(({ id, title, text, code, explanation }) => (
              <motion.div 
                key={id}
                id={`section-${id}`}  // Add this line
                initial={{ opacity: 1, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="flex flex-col gap-6"
              >
                <h2 className="text-3xl font-bold">{title}</h2>
                <div className="text-lg whitespace-pre-wrap">{text}</div>
                
                {code && (
                  <div className="relative">
                    <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
                      <code className="text-sm">{code}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(code, id)}
                      className="absolute top-2 right-2 p-2 bg-white rounded-md hover:bg-gray-200"
                    >
                      <FaCopy />
                      {copiedStates[id] && (
                        <span className="absolute -right-16 top-2 text-green-600">
                          Copied!
                        </span>
                      )}
                    </button>
                  </div>
                )}
                
                {explanation && (
                  <p className="text-gray-600 italic">{explanation}</p>
                )}
              </motion.div>
            ))}
          </div>
          {/* Back to Top Button */}
          <div className="flex justify-center mt-12 mb-8">
            <button
              onClick={scrollToTop}
              className=" text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors duration-300 hover:scale-110" 
              style={{ background: 'rgb(1, 50, 32)' } }>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AArticleMLforecast;